import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, ProtectedRoute, useAuth } from "./lib/auth";
import { WebSocketProvider } from "./lib/ws";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import Layout from "./components/Layout";
import "./App.css";
// Lazy-load every page so a phone downloads only the screen it opens (smaller, faster first load).
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Leads = lazy(() => import("./pages/Leads"));
const LeadForm = lazy(() => import("./pages/LeadForm"));
const LeadDetail = lazy(() => import("./pages/LeadDetail"));
const FollowUps = lazy(() => import("./pages/FollowUps"));
const MyDay = lazy(() => import("./pages/MyDay"));
const Customers = lazy(() => import("./pages/Customers"));
const ClientHistory = lazy(() => import("./pages/ClientHistory"));
const Users = lazy(() => import("./pages/Users"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Invoices = lazy(() => import("./pages/Invoices"));
const WorkReports = lazy(() => import("./pages/WorkReports"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Settings = lazy(() => import("./pages/Settings"));
const Profile = lazy(() => import("./pages/Profile"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Itineraries = lazy(() => import("./pages/Itineraries"));
const ItineraryBuilder = lazy(() => import("./pages/ItineraryBuilder"));
const PassportVault = lazy(() => import("./pages/PassportVault"));
const VendorVault = lazy(() => import("./pages/VendorVault"));
const ArjunAI = lazy(() => import("./pages/ArjunAI"));
const FollowUpCadence = lazy(() => import("./pages/FollowUpCadence"));
const LeadScanner = lazy(() => import("./pages/LeadScanner"));
const AadhaarScanner = lazy(() => import("./pages/AadhaarScanner"));
const RateTracker = lazy(() => import("./pages/RateTracker"));

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm text-gray-500">Loading…</div>;
  if (user) return <Navigate to={`/${user.role}/dashboard`} replace />;
  return <Navigate to="/admin/login" replace />;
}

function Wrapped({ children }) {
  return (
    <WebSocketProvider>
      <Layout>{children}</Layout>
    </WebSocketProvider>
  );
}

function App() {
  useEffect(() => { document.title = "Spark Holidays CRM"; }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-gray-400">Loading…</div>}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/admin/login" element={<Login role="admin" />} />
          <Route path="/executive/login" element={<Login role="executive" />} />

          <Route path="/admin/dashboard" element={<ProtectedRoute role="admin"><Wrapped><Dashboard /></Wrapped></ProtectedRoute>} />
          <Route path="/executive/dashboard" element={<ProtectedRoute role="executive"><Wrapped><Dashboard /></Wrapped></ProtectedRoute>} />

          <Route path="/leads" element={<ProtectedRoute><Wrapped><Leads /></Wrapped></ProtectedRoute>} />
          <Route path="/leads/new" element={<ProtectedRoute><Wrapped><LeadForm /></Wrapped></ProtectedRoute>} />
          <Route path="/leads/:id/edit" element={<ProtectedRoute><Wrapped><LeadForm edit /></Wrapped></ProtectedRoute>} />
          <Route path="/leads/:id" element={<ProtectedRoute><Wrapped><LeadDetail /></Wrapped></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute role="admin"><Wrapped><Customers /></Wrapped></ProtectedRoute>} />
          <Route path="/client-history" element={<ProtectedRoute role="admin"><Wrapped><ClientHistory /></Wrapped></ProtectedRoute>} />

          <Route path="/my-day" element={<ProtectedRoute><Wrapped><MyDay /></Wrapped></ProtectedRoute>} />
          <Route path="/follow-ups" element={<ProtectedRoute><Wrapped><FollowUps /></Wrapped></ProtectedRoute>} />
          <Route path="/tasks" element={<ProtectedRoute><Wrapped><Tasks /></Wrapped></ProtectedRoute>} />
          <Route path="/invoices" element={<ProtectedRoute><Wrapped><Invoices /></Wrapped></ProtectedRoute>} />
          <Route path="/itineraries" element={<ProtectedRoute><Wrapped><Itineraries /></Wrapped></ProtectedRoute>} />
          <Route path="/itineraries/new" element={<ProtectedRoute><Wrapped><ItineraryBuilder /></Wrapped></ProtectedRoute>} />
          <Route path="/itineraries/:id/edit" element={<ProtectedRoute><Wrapped><ItineraryBuilder edit /></Wrapped></ProtectedRoute>} />
          <Route path="/itineraries/:id" element={<ProtectedRoute><Wrapped><ItineraryBuilder edit /></Wrapped></ProtectedRoute>} />
          <Route path="/tools/passport-vault" element={<ProtectedRoute><Wrapped><PassportVault /></Wrapped></ProtectedRoute>} />
          <Route path="/tools/vendor-vault" element={<ProtectedRoute><Wrapped><VendorVault /></Wrapped></ProtectedRoute>} />
          <Route path="/tools/arjun-ai" element={<ProtectedRoute><Wrapped><ArjunAI /></Wrapped></ProtectedRoute>} />
          <Route path="/tools/follow-up-cadence" element={<ProtectedRoute><Wrapped><FollowUpCadence /></Wrapped></ProtectedRoute>} />
          <Route path="/tools/lead-scanner" element={<ProtectedRoute><Wrapped><LeadScanner /></Wrapped></ProtectedRoute>} />
          <Route path="/tools/aadhaar-scanner" element={<ProtectedRoute><Wrapped><AadhaarScanner /></Wrapped></ProtectedRoute>} />
          <Route path="/tools/rate-tracker" element={<ProtectedRoute><Wrapped><RateTracker /></Wrapped></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute role="admin"><Wrapped><Users /></Wrapped></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute role="admin"><Wrapped><Analytics /></Wrapped></ProtectedRoute>} />
          <Route path="/work-reports" element={<ProtectedRoute><Wrapped><WorkReports /></Wrapped></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Wrapped><Notifications /></Wrapped></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute role="admin"><Wrapped><Settings /></Wrapped></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Wrapped><Profile /></Wrapped></ProtectedRoute>} />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
